import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Loader2, Plus, Trash2, RefreshCw, ExternalLink, Youtube,
  CheckCircle, XCircle, AlertTriangle, ChevronDown, ChevronUp, Search, Eye, EyeOff,
  TrendingUp, TrendingDown, BarChart3, Trophy, Zap, Sparkles, Link2, Mail
} from 'lucide-react';

// Helper: get admin auth headers (token from localStorage as fallback for cookies)
function getAdminHeaders(extra = {}) {
  const headers = { ...extra };
  const token = sessionStorage.getItem('admin_token');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

function adminFetch(url, opts = {}) {
  return fetch(url, {
    ...opts,
    credentials: 'include',
    headers: getAdminHeaders(opts.headers || {}),
  });
}

export default function YouTubeTab() {
  const [channels, setChannels] = useState([]);
  const [importStatus, setImportStatus] = useState(null);
  const [reviewVideos, setReviewVideos] = useState([]);
  const [reviewTotal, setReviewTotal] = useState(0);
  const [loadingChannels, setLoadingChannels] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingReview, setLoadingReview] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ channel_url_or_id: '', default_competition: '', default_country: '', priority: 5 });
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [expandedSection, setExpandedSection] = useState('report');
  const [channelSearch, setChannelSearch] = useState('');
  const [healthReport, setHealthReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(true);
  const [aiTestTitle, setAiTestTitle] = useState('');
  const [aiTestResult, setAiTestResult] = useState(null);
  const [aiTestLoading, setAiTestLoading] = useState(false);
  const [batchStatus, setBatchStatus] = useState(null);
  const [batchStarting, setBatchStarting] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [diagnosticResult, setDiagnosticResult] = useState(null);
  const [runningDiag, setRunningDiag] = useState(false);

  const fetchChannels = useCallback(async () => {
    setLoadingChannels(true);
    try {
      const res = await adminFetch(`/api/youtube/channels`);
      if (res.ok) {
        const data = await res.json();
        setChannels(data.channels || []);
      } else {
        const err = await res.json().catch(() => ({}));
        console.error('Channels fetch error:', res.status, err);
        if (res.status === 401) toast.error('Admin session expired. Please re-login.');
        else toast.error(err.detail || `Failed to load channels (${res.status})`);
      }
    } catch (e) { console.error('Channels network error:', e); toast.error('Network error loading channels. Check console.'); }
    finally { setLoadingChannels(false); }
  }, []);

  const fetchStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const res = await adminFetch(`/api/youtube/import/status`);
      if (res.ok) setImportStatus(await res.json());
      else {
        const err = await res.json().catch(() => ({}));
        console.error('Import status error:', res.status, err);
        if (res.status === 401) console.warn('Admin session issue — status fetch returned 401');
      }
    } catch (e) { console.error('Import status network error:', e); }
    finally { setLoadingStatus(false); }
  }, []);

  const fetchReviewQueue = useCallback(async () => {
    setLoadingReview(true);
    try {
      const res = await adminFetch(`/api/youtube/videos?needs_review=true&limit=50`);
      if (res.ok) {
        const data = await res.json();
        setReviewVideos(data.videos || []);
        setReviewTotal(data.total || 0);
      }
    } catch { /* silent */ }
    finally { setLoadingReview(false); }
  }, []);

  const fetchHealthReport = useCallback(async () => {
    setLoadingReport(true);
    try {
      const res = await adminFetch(`/api/youtube/health-report`);
      if (res.ok) setHealthReport(await res.json());
      else console.error('Health report error:', res.status);
    } catch (e) { console.error('Health report network error:', e); }
    finally { setLoadingReport(false); }
  }, []);

  const fetchBatchStatus = useCallback(async () => {
    try {
      const res = await adminFetch(`/api/youtube/batch-parse/status`);
      if (res.ok) setBatchStatus(await res.json());
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchChannels();
    fetchStatus();
    fetchReviewQueue();
    fetchHealthReport();
    fetchBatchStatus();
  }, [fetchChannels, fetchStatus, fetchReviewQueue, fetchHealthReport, fetchBatchStatus]);

  // Poll batch status while running
  useEffect(() => {
    if (!batchStatus?.is_running) return;
    const interval = setInterval(fetchBatchStatus, 3000);
    return () => clearInterval(interval);
  }, [batchStatus?.is_running, fetchBatchStatus]);

  const handleAddChannel = async (e) => {
    e.preventDefault();
    if (!addForm.channel_url_or_id.trim()) return;
    setAdding(true);
    try {
      const res = await adminFetch(`/api/youtube/channels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addForm),
      });
      const data = await res.json();
      if (res.ok && data.status === 'added') {
        toast.success(`Added: ${data.channel.channel_title}`);
        setShowAddForm(false);
        setAddForm({ channel_url_or_id: '', default_competition: '', default_country: '', priority: 5 });
        fetchChannels();
      } else if (data.status === 'already_exists') {
        toast.info('Channel already exists');
      } else if (res.status === 401) {
        toast.error('Admin session expired. Please re-login.');
      } else {
        toast.error(data.detail || `Failed to add channel (${res.status})`);
      }
    } catch (e) {
      console.error('Add channel error:', e);
      toast.error(`Network error: ${e.message || 'Could not reach server'}`);
    }
    finally { setAdding(false); }
  };

  const toggleChannel = async (channelId, currentActive) => {
    try {
      const res = await adminFetch(`/api/youtube/channels/${channelId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !currentActive }),
      });
      if (res.ok) {
        toast.success(currentActive ? 'Channel deactivated' : 'Channel activated');
        fetchChannels();
      }
    } catch { toast.error('Failed to update channel'); }
  };

  const deleteChannel = async (channelId, title) => {
    if (!window.confirm(`Remove "${title}" from trusted channels?`)) return;
    try {
      const res = await adminFetch(`/api/youtube/channels/${channelId}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        toast.success('Channel removed');
        fetchChannels();
      }
    } catch { toast.error('Failed to delete channel'); }
  };

  const resolveVideo = async (videoId, competition) => {
    try {
      const res = await adminFetch(`/api/youtube/videos/${videoId}/tag`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ competition_name: competition, needs_review: false }),
      });
      if (res.ok) {
        toast.success('Video tagged');
        fetchReviewQueue();
      }
    } catch { toast.error('Failed to tag video'); }
  };

  const deleteVideo = async (videoId) => {
    if (!window.confirm('Delete this imported video?')) return;
    try {
      const res = await adminFetch(`/api/youtube/videos/${videoId}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        toast.success('Video deleted');
        fetchReviewQueue();
      }
    } catch { toast.error('Failed to delete video'); }
  };

  const triggerImport = async () => {
    setImporting(true);
    try {
      const res = await adminFetch(`/api/youtube/import/trigger`, {
        method: 'POST',
      });
      const data = await res.json();
      if (data.status === 'completed') {
        toast.success(`Import done: ${data.imported} new, ${data.deduped || 0} dupes, ${data.tagged || 0} tagged, ${data.ai_parsed || 0} AI parsed`);
      } else {
        toast.info(`Import: ${data.status}`);
      }
      fetchStatus();
      fetchReviewQueue();
      fetchHealthReport();
    } catch { toast.error('Import failed'); }
    finally { setImporting(false); }
  };

  const testAiParser = async () => {
    if (!aiTestTitle.trim()) return;
    setAiTestLoading(true);
    setAiTestResult(null);
    try {
      const res = await adminFetch(`/api/youtube/ai-parse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: aiTestTitle }),
      });
      if (res.status === 401) {
        toast.error('Admin session expired. Please re-login.');
        return;
      }
      const data = await res.json();
      setAiTestResult(data);
    } catch (e) {
      console.error('AI parse error:', e);
      toast.error(`AI parse failed: ${e.message || 'Network error'}`);
    }
    finally { setAiTestLoading(false); }
  };

  const startBatchParse = async () => {
    setBatchStarting(true);
    try {
      const res = await adminFetch(`/api/youtube/batch-parse`, {
        method: 'POST',
      });
      const data = await res.json();
      if (data.status === 'started') {
        toast.success(`Batch AI parsing started: ${data.videos_to_process} videos`);
        fetchBatchStatus();
      } else if (data.status === 'already_running') {
        toast.info('Batch parsing already in progress');
      } else if (data.status === 'nothing_to_parse') {
        toast.info(`All ${data.ai_enriched}/${data.total} videos already enriched`);
      }
    } catch { toast.error('Failed to start batch parse'); }
    finally { setBatchStarting(false); }
  };

  const sendReportEmail = async () => {
    setSendingEmail(true);
    try {
      const res = await adminFetch(`/api/youtube/health-report/send`, {
        method: 'POST',
      });
      const data = await res.json();
      if (data.status === 'success') {
        toast.success(`Report emailed to ${data.recipient}`);
      } else {
        toast.error(`Email failed: ${data.error || data.status}`);
      }
    } catch { toast.error('Failed to send report email'); }
    finally { setSendingEmail(false); }
  };

  const runDiagnostic = async () => {
    setRunningDiag(true);
    setDiagnosticResult(null);
    try {
      const res = await adminFetch(`/api/youtube/diagnostic`);
      if (res.ok) {
        setDiagnosticResult(await res.json());
      } else {
        const err = await res.json().catch(() => ({}));
        setDiagnosticResult({ error: err.detail || `HTTP ${res.status}`, admin_auth: res.status === 401 ? 'FAILED' : 'unknown' });
      }
    } catch (e) {
      setDiagnosticResult({ error: `Network error: ${e.message}`, admin_auth: 'NETWORK_FAIL' });
    }
    finally { setRunningDiag(false); }
  };

  const filteredChannels = channels.filter(ch =>
    !channelSearch || ch.channel_title?.toLowerCase().includes(channelSearch.toLowerCase()) ||
    ch.default_competition?.toLowerCase().includes(channelSearch.toLowerCase()) ||
    ch.default_country?.toLowerCase().includes(channelSearch.toLowerCase())
  );

  const SectionHeader = ({ id, title, count, icon: Icon }) => (
    <button
      onClick={() => setExpandedSection(expandedSection === id ? '' : id)}
      data-testid={`yt-section-${id}`}
      className="w-full flex items-center justify-between p-4 bg-[#1e1e1e] hover:bg-[#252525] rounded-lg transition-colors"
    >
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5 text-red-500" />
        <span className="text-white font-semibold">{title}</span>
        {count !== undefined && (
          <span className="text-xs bg-white/10 text-white/60 px-2 py-0.5 rounded-full">{count}</span>
        )}
      </div>
      {expandedSection === id ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
    </button>
  );

  return (
    <div className="space-y-4" data-testid="youtube-tab">
      {/* Import Status Dashboard */}
      <div className="bg-[#1e1e1e] rounded-xl p-5 border border-white/5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Youtube className="w-5 h-5 text-red-500" />
            YouTube Import Dashboard
          </h3>
          <button
            onClick={triggerImport}
            disabled={importing}
            data-testid="yt-trigger-import-btn"
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
          >
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {importing ? 'Importing...' : 'Run Import Now'}
          </button>
        </div>

        {loadingStatus ? (
          <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-white/40" /></div>
        ) : importStatus ? (
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
            <StatCard label="Total Videos" value={importStatus.total_videos} />
            <StatCard label="Active Channels" value={`${importStatus.active_channels}/${importStatus.total_channels}`} />
            <StatCard label="Linked" value={importStatus.linked_to_matches || 0} />
            <StatCard label="AI Parsed" value={importStatus.ai_parsed || 0} />
            <StatCard label="Needs Review" value={importStatus.needs_review} warn={importStatus.needs_review > 0} />
            <StatCard
              label="Last Import"
              value={importStatus.latest_import?.timestamp
                ? new Date(importStatus.latest_import.timestamp).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                : 'Never'}
            />
          </div>
        ) : (
          <p className="text-white/40 text-sm">No import data available</p>
        )}

        {importStatus?.latest_import && (
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <span className="bg-emerald-900/40 text-emerald-400 px-2 py-1 rounded">+{importStatus.latest_import.imported || 0} imported</span>
            <span className="bg-blue-900/40 text-blue-400 px-2 py-1 rounded">{importStatus.latest_import.deduped || 0} dupes</span>
            <span className="bg-yellow-900/40 text-yellow-400 px-2 py-1 rounded">{importStatus.latest_import.tagged || 0} tagged</span>
            <span className="bg-purple-900/40 text-purple-400 px-2 py-1 rounded">{importStatus.latest_import.linked || 0} linked</span>
            <span className="bg-violet-900/40 text-violet-400 px-2 py-1 rounded">{importStatus.latest_import.ai_parsed || 0} AI</span>
            <span className="bg-orange-900/40 text-orange-400 px-2 py-1 rounded">{importStatus.latest_import.filtered || 0} filtered</span>
            <span className="bg-red-900/40 text-red-400 px-2 py-1 rounded">{importStatus.latest_import.failed || 0} failed</span>
          </div>
        )}

        {importStatus?.recent_imports?.length > 1 && (
          <details className="mt-3">
            <summary className="text-xs text-white/40 cursor-pointer hover:text-white/60">Recent import history</summary>
            <div className="mt-2 space-y-1">
              {importStatus.recent_imports.slice(1).map((log, i) => (
                <div key={i} className="text-xs text-white/30 flex gap-3">
                  <span>{new Date(log.timestamp).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  <span>+{log.imported || 0}</span>
                  <span>{log.deduped || 0} dupes</span>
                  <span>{log.failed || 0} fail</span>
                </div>
              ))}
            </div>
          </details>
        )}

        {/* Diagnostic Tool */}
        <div className="mt-3 pt-3 border-t border-white/5">
          <div className="flex items-center gap-2">
            <button
              onClick={runDiagnostic}
              disabled={runningDiag}
              data-testid="yt-diagnostic-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 disabled:opacity-50 text-white/60 rounded-lg text-xs transition-colors"
            >
              {runningDiag ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertTriangle className="w-3 h-3" />}
              Run Diagnostic
            </button>
            {diagnosticResult && (
              <span className={`text-xs ${diagnosticResult.error ? 'text-red-400' : 'text-emerald-400'}`}>
                {diagnosticResult.error ? 'Issues found' : 'All checks passed'}
              </span>
            )}
          </div>
          {diagnosticResult && (
            <div className="mt-2 bg-[#1a1a1a] rounded-lg p-3 text-xs font-mono space-y-1 max-h-48 overflow-y-auto">
              {Object.entries(diagnosticResult).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4">
                  <span className="text-white/40">{k}:</span>
                  <span className={`text-right ${
                    v === 'ok' || v === true || v === 200 ? 'text-emerald-400' :
                    v === false || v === 'FAILED' || v === 'NETWORK_FAIL' ? 'text-red-400' : 'text-white/70'
                  }`}>{String(v)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Weekly Health Report */}
      <SectionHeader id="report" title="Weekly Health Report" count={null} icon={BarChart3} />
      {expandedSection === 'report' && (
        <div className="bg-[#1a1a1a] rounded-xl border border-white/5 p-4" data-testid="yt-health-report">
          {loadingReport ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-white/40" /></div>
          ) : !healthReport ? (
            <p className="text-white/40 text-sm text-center py-6">No report data available</p>
          ) : (
            <div className="space-y-5">
              {/* Growth Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard label="This Week" value={healthReport.summary.imported_this_week} />
                <StatCard label="Last Week" value={healthReport.summary.imported_last_week} />
                <div className="bg-[#252525] rounded-lg p-3">
                  <p className="text-white/40 text-xs mb-1">Growth</p>
                  <div className="flex items-center gap-1.5">
                    {healthReport.summary.growth_percent >= 0
                      ? <TrendingUp className="w-4 h-4 text-emerald-400" />
                      : <TrendingDown className="w-4 h-4 text-red-400" />}
                    <span className={`text-lg font-bold ${healthReport.summary.growth_percent >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {healthReport.summary.growth_percent > 0 ? '+' : ''}{healthReport.summary.growth_percent}%
                    </span>
                  </div>
                </div>
                <StatCard label="All Time" value={healthReport.summary.total_all_time} />
              </div>

              {/* Content Breakdown */}
              <div>
                <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2">Content Breakdown (This Week)</h4>
                <div className="grid grid-cols-5 gap-2">
                  {[
                    { label: 'Highlights', val: healthReport.content_breakdown.highlights, color: 'bg-emerald-500' },
                    { label: 'Replays', val: healthReport.content_breakdown.replays, color: 'bg-blue-500' },
                    { label: 'Interviews', val: healthReport.content_breakdown.interviews, color: 'bg-purple-500' },
                    { label: 'News', val: healthReport.content_breakdown.news, color: 'bg-yellow-500' },
                    { label: 'Analysis', val: healthReport.content_breakdown.analysis, color: 'bg-orange-500' },
                  ].map(item => (
                    <div key={item.label} className="bg-[#222] rounded-lg p-2.5 text-center">
                      <div className={`w-2 h-2 ${item.color} rounded-full mx-auto mb-1.5`} />
                      <p className="text-white font-bold text-sm">{item.val}</p>
                      <p className="text-white/30 text-[10px]">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Top Competitions */}
                <div>
                  <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Trophy className="w-3.5 h-3.5" /> Top Competitions
                  </h4>
                  <div className="bg-[#222] rounded-lg overflow-hidden">
                    {healthReport.top_competitions.length === 0 ? (
                      <p className="text-white/30 text-xs p-3">No data</p>
                    ) : healthReport.top_competitions.map((comp, i) => (
                      <div key={comp.competition} className="flex items-center justify-between px-3 py-2 border-b border-white/5 last:border-0">
                        <div className="flex items-center gap-2">
                          <span className="text-white/20 text-xs w-4">{i + 1}</span>
                          <span className="text-white text-sm">{comp.competition}</span>
                        </div>
                        <span className="text-white/50 text-xs font-mono">{comp.count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Top Channels by Views */}
                <div>
                  <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <TrendingUp className="w-3.5 h-3.5" /> Top Channels by Views
                  </h4>
                  <div className="bg-[#222] rounded-lg overflow-hidden">
                    {healthReport.top_channels_by_views.length === 0 ? (
                      <p className="text-white/30 text-xs p-3">No data</p>
                    ) : healthReport.top_channels_by_views.map((ch, i) => (
                      <div key={ch.channel} className="flex items-center justify-between px-3 py-2 border-b border-white/5 last:border-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-white/20 text-xs w-4">{i + 1}</span>
                          <span className="text-white text-sm truncate">{ch.channel}</span>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0 text-xs">
                          <span className="text-white/30">{ch.video_count} vids</span>
                          <span className="text-white/50 font-mono">{formatViews(ch.total_views)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Top 5 Most Viewed Videos */}
              <div>
                <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2">Top 5 Most Viewed Videos</h4>
                <div className="space-y-1.5">
                  {healthReport.top_viewed_videos.map((v, i) => (
                    <div key={v.id} className="flex items-center gap-3 bg-[#222] rounded-lg p-2.5" data-testid={`yt-top-video-${i}`}>
                      <span className="text-white/20 font-bold text-lg w-6 text-center flex-shrink-0">{i + 1}</span>
                      {v.thumbnail_url && (
                        <img src={v.thumbnail_url} alt="" className="w-20 h-12 object-cover rounded flex-shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{v.title}</p>
                        <div className="flex items-center gap-2 text-xs text-white/30 mt-0.5">
                          <span>{v.youtube_channel}</span>
                          {v.competition_name && <span className="text-emerald-400/70">{v.competition_name}</span>}
                        </div>
                      </div>
                      <span className="text-white/60 text-sm font-mono flex-shrink-0">{formatViews(v.views)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Channels with Errors */}
              {healthReport.channels_with_errors.length > 0 && (
                <div>
                  <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <XCircle className="w-3.5 h-3.5 text-red-400" /> Channels with Errors
                  </h4>
                  <div className="bg-[#222] rounded-lg overflow-hidden">
                    {healthReport.channels_with_errors.map((ch) => (
                      <div key={ch.channel} className="flex items-center justify-between px-3 py-2 border-b border-white/5 last:border-0">
                        <span className="text-white text-sm">{ch.channel}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-red-400/70 text-xs">{ch.error}</span>
                          {ch.last_seen && <span className="text-white/20 text-[10px]">{new Date(ch.last_seen).toLocaleDateString()}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quota Usage */}
              <div>
                <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Zap className="w-3.5 h-3.5 text-yellow-400" /> YouTube API Quota (This Week)
                </h4>
                <div className="bg-[#222] rounded-lg p-3">
                  <div className="flex items-center gap-4 mb-2">
                    <span className="text-white text-sm font-bold">{healthReport.quota_usage.est_total_units.toLocaleString()}</span>
                    <span className="text-white/30 text-xs">estimated units used</span>
                    <span className="text-white/20 text-xs ml-auto">Daily limit: {healthReport.quota_usage.daily_limit.toLocaleString()}</span>
                  </div>
                  <div className="w-full bg-[#1a1a1a] rounded-full h-2 mb-2">
                    <div
                      className="bg-gradient-to-r from-emerald-500 to-yellow-500 h-2 rounded-full transition-all"
                      style={{ width: `${Math.min((healthReport.quota_usage.est_total_units / (healthReport.quota_usage.daily_limit * 7)) * 100, 100)}%` }}
                    />
                  </div>
                  <div className="flex gap-4 text-xs text-white/30">
                    <span>{healthReport.quota_usage.import_runs} runs</span>
                    <span>{healthReport.quota_usage.est_playlist_units} playlist units</span>
                    <span>{healthReport.quota_usage.est_video_units} video units</span>
                    {healthReport.quota_usage.est_seed_units > 0 && <span>{healthReport.quota_usage.est_seed_units} seed units</span>}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <p className="text-white/15 text-[10px]">
                  Report generated {new Date(healthReport.generated_at).toLocaleString()} &middot; Recipient: {healthReport.recipient}
                </p>
                <button
                  onClick={sendReportEmail}
                  disabled={sendingEmail}
                  data-testid="yt-send-email-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-xs transition-colors"
                >
                  {sendingEmail ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                  {sendingEmail ? 'Sending...' : 'Email Report'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Channels Section */}
      <SectionHeader id="channels" title="Trusted Channels" count={channels.length} icon={Youtube} />
      {expandedSection === 'channels' && (
        <div className="bg-[#1a1a1a] rounded-xl border border-white/5 p-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <input
                type="text"
                placeholder="Search channels..."
                value={channelSearch}
                onChange={(e) => setChannelSearch(e.target.value)}
                data-testid="yt-channel-search"
                className="w-full pl-10 pr-4 py-2 bg-[#222] border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:outline-none focus:border-red-500/50"
              />
            </div>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              data-testid="yt-add-channel-btn"
              className="flex items-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Channel
            </button>
          </div>

          {showAddForm && (
            <form onSubmit={handleAddChannel} className="bg-[#222] rounded-lg p-4 space-y-3 border border-white/10" data-testid="yt-add-channel-form">
              <input
                type="text"
                placeholder="Channel URL, handle (e.g. @CAFOnline), or Channel ID"
                value={addForm.channel_url_or_id}
                onChange={(e) => setAddForm(prev => ({ ...prev, channel_url_or_id: e.target.value }))}
                data-testid="yt-channel-input"
                className="w-full px-3 py-2 bg-[#1a1a1a] border border-white/10 rounded text-white text-sm placeholder-white/30 focus:outline-none focus:border-red-500/50"
                required
              />
              <div className="grid grid-cols-3 gap-3">
                <input
                  type="text"
                  placeholder="Default competition"
                  value={addForm.default_competition}
                  onChange={(e) => setAddForm(prev => ({ ...prev, default_competition: e.target.value }))}
                  className="px-3 py-2 bg-[#1a1a1a] border border-white/10 rounded text-white text-sm placeholder-white/30 focus:outline-none focus:border-red-500/50"
                />
                <input
                  type="text"
                  placeholder="Default country"
                  value={addForm.default_country}
                  onChange={(e) => setAddForm(prev => ({ ...prev, default_country: e.target.value }))}
                  className="px-3 py-2 bg-[#1a1a1a] border border-white/10 rounded text-white text-sm placeholder-white/30 focus:outline-none focus:border-red-500/50"
                />
                <div className="flex items-center gap-2">
                  <label className="text-xs text-white/40">Priority</label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={addForm.priority}
                    onChange={(e) => setAddForm(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                    className="w-16 px-2 py-2 bg-[#1a1a1a] border border-white/10 rounded text-white text-sm focus:outline-none focus:border-red-500/50"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={adding}
                  data-testid="yt-add-channel-submit"
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded text-sm"
                >
                  {adding ? 'Adding...' : 'Add Channel'}
                </button>
                <button type="button" onClick={() => setShowAddForm(false)} className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white/60 rounded text-sm">
                  Cancel
                </button>
              </div>
            </form>
          )}

          {loadingChannels ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-white/40" /></div>
          ) : (
            <div className="space-y-1 max-h-[400px] overflow-y-auto">
              {filteredChannels.map((ch) => (
                <div
                  key={ch.channel_id}
                  data-testid={`yt-channel-${ch.channel_id}`}
                  className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                    ch.active !== false ? 'bg-[#222] hover:bg-[#282828]' : 'bg-[#1c1c1c] opacity-60'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-sm font-medium truncate">{ch.channel_title}</span>
                      {ch.source === 'trusted_seed' && (
                        <span className="text-[10px] bg-yellow-900/40 text-yellow-400 px-1.5 py-0.5 rounded">SEED</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-white/30">
                      {ch.default_competition && <span>{ch.default_competition}</span>}
                      {ch.default_country && <span>{ch.default_country}</span>}
                      <span>P{ch.priority}</span>
                      {ch.last_fetched_at && (
                        <span>Last: {new Date(ch.last_fetched_at).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    <button
                      onClick={() => toggleChannel(ch.channel_id, ch.active !== false)}
                      data-testid={`yt-toggle-${ch.channel_id}`}
                      className="p-1.5 rounded hover:bg-white/10 transition-colors"
                      title={ch.active !== false ? 'Deactivate' : 'Activate'}
                    >
                      {ch.active !== false
                        ? <Eye className="w-4 h-4 text-emerald-400" />
                        : <EyeOff className="w-4 h-4 text-white/30" />}
                    </button>
                    <button
                      onClick={() => deleteChannel(ch.channel_id, ch.channel_title)}
                      data-testid={`yt-delete-${ch.channel_id}`}
                      className="p-1.5 rounded hover:bg-red-900/40 transition-colors"
                    >
                      <Trash2 className="w-4 h-4 text-red-400/60 hover:text-red-400" />
                    </button>
                  </div>
                </div>
              ))}
              {filteredChannels.length === 0 && (
                <p className="text-center text-white/30 text-sm py-4">No channels found</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* AI Parser Test Tool */}
      <SectionHeader id="ai-parser" title="AI Title Parser" count={null} icon={Sparkles} />
      {expandedSection === 'ai-parser' && (
        <div className="bg-[#1a1a1a] rounded-xl border border-white/5 p-4 space-y-3" data-testid="yt-ai-parser">
          <p className="text-white/40 text-xs">Test the GPT-5.2 AI parser. Paste a video title to see what metadata it extracts.</p>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder='e.g. "Nigeria 2-1 Egypt | AFCON 2025 Quarterfinal Highlights"'
              value={aiTestTitle}
              onChange={(e) => setAiTestTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && testAiParser()}
              data-testid="yt-ai-parse-input"
              className="flex-1 px-3 py-2 bg-[#222] border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:outline-none focus:border-purple-500/50"
            />
            <button
              onClick={testAiParser}
              disabled={aiTestLoading || !aiTestTitle.trim()}
              data-testid="yt-ai-parse-btn"
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg text-sm transition-colors"
            >
              {aiTestLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Parse
            </button>
          </div>
          {aiTestResult && (
            <div className="bg-[#222] rounded-lg p-4 border border-white/10">
              {aiTestResult.status === 'success' ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    <span className="text-emerald-400 text-xs font-medium">Parsed successfully</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {[
                      { label: 'Team A', value: aiTestResult.parsed.team_a, icon: '🏠' },
                      { label: 'Team B', value: aiTestResult.parsed.team_b, icon: '✈️' },
                      { label: 'Competition', value: aiTestResult.parsed.competition },
                      { label: 'Season', value: aiTestResult.parsed.season_year },
                      { label: 'Stage', value: aiTestResult.parsed.match_stage },
                      { label: 'Score', value: aiTestResult.parsed.score },
                      { label: 'Date', value: aiTestResult.parsed.match_date },
                      { label: 'Penalties', value: aiTestResult.parsed.has_penalties ? 'Yes' : 'No' },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex items-center justify-between bg-[#1a1a1a] rounded px-3 py-1.5">
                        <span className="text-white/40 text-xs">{label}</span>
                        <span className={`text-xs font-medium ${value && value !== 'No' ? 'text-white' : 'text-white/20'}`}>
                          {value || '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-red-400" />
                  <span className="text-red-400 text-xs">{aiTestResult.message || 'Parse failed'}</span>
                </div>
              )}
            </div>
          )}
          {/* Batch AI Parse */}
          <div className="bg-[#222] rounded-lg p-4 border border-white/10 space-y-3" data-testid="yt-batch-parse">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-white text-sm font-medium flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-400" />
                  Batch AI Enrichment
                </h4>
                <p className="text-white/30 text-xs mt-0.5">
                  Retroactively parse all videos missing teams or competition metadata using GPT-5.2
                </p>
              </div>
              <button
                onClick={startBatchParse}
                disabled={batchStarting || batchStatus?.is_running}
                data-testid="yt-batch-parse-btn"
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg text-sm transition-colors"
              >
                {(batchStarting || batchStatus?.is_running)
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Sparkles className="w-4 h-4" />}
                {batchStatus?.is_running ? 'Running...' : 'Start Batch Parse'}
              </button>
            </div>

            {batchStatus && (
              <div className="space-y-2">
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="bg-[#1a1a1a] rounded px-2 py-2">
                    <p className="text-white font-bold text-sm">{batchStatus.ai_enriched}</p>
                    <p className="text-white/30 text-[10px]">AI Enriched</p>
                  </div>
                  <div className="bg-[#1a1a1a] rounded px-2 py-2">
                    <p className="text-white font-bold text-sm">{batchStatus.remaining}</p>
                    <p className="text-white/30 text-[10px]">Remaining</p>
                  </div>
                  <div className="bg-[#1a1a1a] rounded px-2 py-2">
                    <p className="text-white font-bold text-sm">{batchStatus.total_youtube_videos}</p>
                    <p className="text-white/30 text-[10px]">Total Videos</p>
                  </div>
                  <div className="bg-[#1a1a1a] rounded px-2 py-2">
                    <p className={`font-bold text-sm ${batchStatus.is_running ? 'text-purple-400' : 'text-white/40'}`}>
                      {batchStatus.is_running ? 'Running' : batchStatus.latest_job?.status || 'Never run'}
                    </p>
                    <p className="text-white/30 text-[10px]">Status</p>
                  </div>
                </div>

                {batchStatus.is_running && batchStatus.latest_job && (
                  <div>
                    <div className="flex items-center justify-between text-xs text-white/40 mb-1">
                      <span>Processing: {batchStatus.latest_job.processed}/{batchStatus.latest_job.total}</span>
                      <span>{batchStatus.latest_job.total > 0 ? Math.round((batchStatus.latest_job.processed / batchStatus.latest_job.total) * 100) : 0}%</span>
                    </div>
                    <div className="w-full bg-[#1a1a1a] rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${batchStatus.latest_job.total > 0 ? (batchStatus.latest_job.processed / batchStatus.latest_job.total) * 100 : 0}%` }}
                      />
                    </div>
                    <div className="flex gap-3 mt-1.5 text-[10px] text-white/30">
                      <span className="text-emerald-400">{batchStatus.latest_job.enriched} enriched</span>
                      <span className="text-blue-400">{batchStatus.latest_job.linked} linked</span>
                      <span className="text-white/20">{batchStatus.latest_job.skipped} skipped</span>
                      <span className="text-red-400/60">{batchStatus.latest_job.failed} failed</span>
                    </div>
                  </div>
                )}

                {!batchStatus.is_running && batchStatus.latest_job?.status === 'completed' && (
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="bg-emerald-900/40 text-emerald-400 px-2 py-1 rounded">{batchStatus.latest_job.enriched} enriched</span>
                    <span className="bg-blue-900/40 text-blue-400 px-2 py-1 rounded">{batchStatus.latest_job.linked} linked</span>
                    <span className="bg-white/5 text-white/30 px-2 py-1 rounded">{batchStatus.latest_job.skipped} skipped</span>
                    <span className="bg-red-900/40 text-red-400 px-2 py-1 rounded">{batchStatus.latest_job.failed} failed</span>
                    {batchStatus.latest_job.completed_at && (
                      <span className="text-white/20 px-2 py-1">
                        Completed {new Date(batchStatus.latest_job.completed_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <p className="text-white/15 text-[10px]">
            Pipeline: Rule detection → DB match → AI fallback (GPT-5.2) → Admin manual link.
            AI only runs when rule-based detection fails.
          </p>
        </div>
      )}

      {/* Needs Review Section */}
      <SectionHeader id="review" title="Needs Review" count={reviewTotal} icon={AlertTriangle} />
      {expandedSection === 'review' && (
        <div className="bg-[#1a1a1a] rounded-xl border border-white/5 p-4">
          {loadingReview ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-white/40" /></div>
          ) : reviewVideos.length === 0 ? (
            <div className="text-center py-6">
              <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              <p className="text-white/40 text-sm">All videos are tagged. No review needed.</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {reviewVideos.map((v) => (
                <ReviewVideoCard
                  key={v.id}
                  video={v}
                  tags={importStatus?.supported_tags || []}
                  onResolve={resolveVideo}
                  onDelete={deleteVideo}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatViews(n) {
  if (!n) return '0';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toLocaleString();
}

function StatCard({ label, value, warn }) {
  return (
    <div className="bg-[#252525] rounded-lg p-3">
      <p className="text-white/40 text-xs mb-1">{label}</p>
      <p className={`text-lg font-bold ${warn ? 'text-yellow-400' : 'text-white'}`}>{value}</p>
    </div>
  );
}

function ReviewVideoCard({ video, tags, onResolve, onDelete }) {
  const [selectedTag, setSelectedTag] = useState('');

  return (
    <div className="flex items-start gap-3 p-3 bg-[#222] rounded-lg" data-testid={`yt-review-${video.id}`}>
      <img
        src={video.thumbnail_url}
        alt=""
        className="w-28 h-16 object-cover rounded flex-shrink-0"
        onError={(e) => { e.target.style.display = 'none'; }}
      />
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium truncate">{video.title}</p>
        <div className="flex items-center gap-2 mt-1 text-xs text-white/30">
          <span>{video.youtube_channel}</span>
          {video.category && <span className="bg-white/5 px-1.5 py-0.5 rounded">{video.category}</span>}
          {video.competition_name && <span className="text-emerald-400">{video.competition_name}</span>}
        </div>
        <div className="flex items-center gap-2 mt-2">
          <select
            value={selectedTag}
            onChange={(e) => setSelectedTag(e.target.value)}
            className="px-2 py-1 bg-[#1a1a1a] border border-white/10 rounded text-white text-xs focus:outline-none"
          >
            <option value="">Tag competition...</option>
            {tags.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <button
            onClick={() => selectedTag && onResolve(video.id, selectedTag)}
            disabled={!selectedTag}
            className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-30 text-white rounded text-xs"
          >
            Approve
          </button>
          <button
            onClick={() => onDelete(video.id)}
            className="px-2 py-1 bg-red-600/30 hover:bg-red-600/50 text-red-400 rounded text-xs"
          >
            Delete
          </button>
          <a
            href={`https://youtube.com/watch?v=${video.youtube_video_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1 hover:bg-white/10 rounded transition-colors"
          >
            <ExternalLink className="w-3 h-3 text-white/30" />
          </a>
        </div>
      </div>
    </div>
  );
}
